"use client";

import ProductSummaryCard from "@/components/templates/Checkout/components/product-summary-card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { usePrismicProducts, useProducts } from "@/hooks/useProduct";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { cn } from "@/lib/utils";
import { useCheckoutStore } from "@/stores/checkout-store";
import { StoreOrder } from "@medusajs/types";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";

type OrderDetailsProps = {
  order: StoreOrder;
  locale: Locales;
};

const OrderDetails = ({ order, locale }: OrderDetailsProps) => {
  const { justCompletedOrder, setJustCompletedOrder, resetFormData } = useCheckoutStore();

  useEffect(() => {
    if (justCompletedOrder) {
      resetFormData();
      setJustCompletedOrder(false);
    }
  }, [justCompletedOrder, setJustCompletedOrder, resetFormData]); // reset checkout form data when order is completed not if the customer visits the Thank You page again

  const medusaProductIds =
    order.items?.map((item) => item.product_id).filter((id): id is string => id !== null && id !== undefined) || [];

  const { data: medusaProducts, isLoading: isMedusaProductsLoading } = useProducts(locale, medusaProductIds);
  const { data: prismicProducts, isLoading: isPrismicProductsLoading } = usePrismicProducts(locale, medusaProductIds);

  if (isMedusaProductsLoading || isPrismicProductsLoading) {
    return (
      <div className="flex h-[510px] items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (!medusaProducts) {
    return (
      <div className="flex h-[510px] items-center justify-center">
        <p>{__("No product data available", locale)}</p>
      </div>
    );
  }

  const orderItemsCount = (order?.items && order.items.length) || 0;

  return (
    <ScrollArea className={cn("max-md:border-t", orderItemsCount > 6 && "h-[510px]")}>
      <ul className="max-md:border-primary-light/50 space-y-4 max-md:pt-4 sm:pr-3 md:space-y-3">
        {order.items
          ?.filter((item) => item.product_id !== null && item.product_id !== undefined)
          .map((item) => {
            const medusaProduct = medusaProducts[item.product_id!];
            const prismicProduct = prismicProducts?.find((product) => product.data?.medusaId === medusaProduct?.id);

            if (!medusaProduct) {
              return null;
            }

            return (
              <ProductSummaryCard
                locale={locale}
                key={item.id}
                variant="secondary"
                prismicProduct={prismicProduct}
                medusaProduct={medusaProduct}
                orderItem={item}
                hideControls
              />
            );
          })}
      </ul>
    </ScrollArea>
  );
};

export default OrderDetails;
